import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { Layers, Pencil, Trash2 } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { API_BASE } from "@/config/api"
import { opsErrorMessage } from "@/lib/ops-error"
import { useEntitlements } from "@/hooks/use-entitlements"
import { toast } from "sonner"

const API = API_BASE

const RESOURCE_TYPES = ["room", "cabinet", "equipment", "telehealth"]

const emptyForm = () => ({
  name: "",
  resource_type: "room",
  department: "",
})

export default function SchedulingResources() {
  const { t } = useTranslation()
  const { hasFeature } = useEntitlements()
  const canNetwork = hasFeature("multi_location")
  const [resources, setResources] = useState([])
  const [summary, setSummary] = useState({ total: 0, active: 0 })
  const [departments, setDepartments] = useState([])
  const [allSites, setAllSites] = useState(false)
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(emptyForm())
  const [editingId, setEditingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`${API}/scheduling/resources`, {
        params: allSites ? { all_sites: true } : {},
      })
      if (Array.isArray(data)) {
        setResources(data)
        setSummary({ total: data.length, active: data.filter((r) => r.active !== false).length })
      } else {
        setResources(data.resources || [])
        setSummary(data.summary || { total: 0, active: 0 })
      }
      try {
        const dept = await axios.get(`${API}/ops/department-suggestions`)
        setDepartments(dept.data.departments || [])
      } catch {
        setDepartments([])
      }
    } catch {
      toast.error(t("app.schedulingResources.toast.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [t, allSites])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setForm(emptyForm())
    setEditingId(null)
  }

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("app.schedulingResources.toast.nameRequired"))
    const body = {
      name: form.name.trim(),
      resource_type: form.resource_type,
      department: form.department.trim(),
    }
    try {
      if (editingId) {
        await axios.patch(`${API}/scheduling/resources/${editingId}`, body)
        toast.success(t("app.schedulingResources.toast.updated"))
      } else {
        await axios.post(`${API}/scheduling/resources`, body)
        toast.success(t("app.schedulingResources.toast.created"))
      }
      resetForm()
      load()
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.schedulingResources.toast.saveFailed"))
    }
  }

  const startEdit = (r) => {
    setEditingId(r.id)
    setForm({
      name: r.name || "",
      resource_type: r.resource_type || "room",
      department: r.department || "",
    })
  }

  const toggleActive = async (r) => {
    try {
      await axios.patch(`${API}/scheduling/resources/${r.id}`, { active: !r.active })
      load()
    } catch {
      toast.error(t("app.schedulingResources.toast.saveFailed"))
    }
  }

  const deleteResource = async (id) => {
    try {
      await axios.delete(`${API}/scheduling/resources/${id}`)
      if (editingId === id) resetForm()
      toast.success(t("app.schedulingResources.toast.deleted"))
      load()
    } catch (err) {
      toast.error(opsErrorMessage(err, t, "app.schedulingResources.toast.saveFailed"))
    }
  }

  const typeLabel = (type) => t(`app.schedulingResources.types.${type}`, { defaultValue: type })

  return (
    <FeatureGate
      feature="scheduling_resources"
      title={t("app.schedulingResources.gateTitle")}
      description={t("app.schedulingResources.gateDescription")}
    >
      <AppLayout title={t("app.schedulingResources.title")}>
        <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
          {t("app.schedulingResources.intro")}{" "}
          <Link to="/app/appointments" className="underline underline-offset-2">
            {t("app.nav.appointments")}
          </Link>
          {" · "}
          <Link to="/app/queues" className="underline underline-offset-2">
            {t("app.nav.queues")}
          </Link>
        </p>

        <PilotBanner className="mb-4 max-w-2xl">{t("app.schedulingResources.pilotHint")}</PilotBanner>

        {canNetwork && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
            <input type="checkbox" checked={allSites} onChange={(e) => setAllSites(e.target.checked)} />
            {t("app.ops.allSites", { defaultValue: "Toate locațiile" })}
          </label>
        )}

        <div className="grid sm:grid-cols-2 gap-3 mb-6 max-w-md">
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.schedulingResources.metrics.total")}</p>
            <p className="font-display text-2xl mt-1">{summary.total}</p>
          </article>
          <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{t("app.schedulingResources.metrics.active")}</p>
            <p className="font-display text-2xl mt-1">{summary.active}</p>
          </article>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          <section className="rounded-xl border border-border p-6 bg-card space-y-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Layers className="h-4 w-4" />
              {editingId ? t("app.schedulingResources.editResource") : t("app.schedulingResources.newResource")}
            </h3>
            <div>
              <Label className="text-xs">{t("app.schedulingResources.fieldName")}</Label>
              <Input
                className="mt-1"
                placeholder={t("app.schedulingResources.namePlaceholder")}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("app.schedulingResources.fieldType")}</Label>
                <Select
                  value={form.resource_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, resource_type: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOURCE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {typeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("app.schedulingResources.fieldDepartment")}</Label>
                <Input
                  className="mt-1"
                  list="ops-departments-resources"
                  placeholder={t("app.schedulingResources.departmentPlaceholder")}
                  value={form.department}
                  onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                />
                <datalist id="ops-departments-resources">
                  {departments.map((d) => (
                    <option key={d} value={d} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button className="rounded-full" onClick={save}>
                {editingId ? t("app.schedulingResources.save") : t("app.schedulingResources.createBtn")}
              </Button>
              {editingId && (
                <Button variant="ghost" className="rounded-full" onClick={resetForm}>
                  {t("app.schedulingResources.cancel")}
                </Button>
              )}
            </div>
          </section>

          <div>
            <h3 className="text-sm font-medium mb-2">{t("app.schedulingResources.listTitle")}</h3>
            <ul className="divide-y divide-border rounded-xl border border-border bg-card text-sm">
              {loading && (
                <li className="px-4 py-8 text-center text-muted-foreground">
                  {t("app.schedulingResources.loading")}
                </li>
              )}
              {!loading &&
                resources.map((r) => (
                  <li key={r.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <button
                        type="button"
                        className="text-left flex-1 min-w-0 hover:opacity-80"
                        onClick={() => startEdit(r)}
                      >
                        <p className="font-medium flex items-center gap-2 flex-wrap">
                          {r.name}
                          {r.active === false && (
                            <Badge variant="secondary" className="text-[10px]">
                              {t("app.schedulingResources.inactive")}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {typeLabel(r.resource_type)}
                          {r.department ? ` · ${r.department}` : ""}
                        </p>
                        {(r.upcoming_count || 0) > 0 && (
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                            {t("app.schedulingResources.upcomingCount", { count: r.upcoming_count })}
                          </p>
                        )}
                      </button>
                      <div className="flex items-center gap-1 shrink-0">
                        <Switch
                          checked={r.active !== false}
                          onCheckedChange={() => toggleActive(r)}
                          aria-label={t("app.schedulingResources.toggleActive")}
                        />
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(r)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("app.schedulingResources.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("app.schedulingResources.deleteBody")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("app.schedulingResources.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteResource(r.id)}>
                                {t("app.schedulingResources.confirmDelete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </li>
                ))}
              {!loading && !resources.length && (
                <li className="px-4 py-8 text-center text-muted-foreground">
                  {t("app.schedulingResources.empty")}
                </li>
              )}
            </ul>
          </div>
        </div>
      </AppLayout>
    </FeatureGate>
  )
}
